import React, { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { shapeClass, type Shape } from "../../lib/shape.js";
import { CATEGORICAL_TINTS, type CategoricalTint } from "../../lib/tints.js";

/** Semantic colour of a Badge: the shared status tones. `accent` is not a
 *  tone (packages/ui/CLAUDE.md C7). */
export type BadgeTone = StatusTone;

/** `soft` (the default): tinted fill and tone text. `solid`: saturated fill
 *  and on-accent text. `outline`: tone border and tone text. `code`: a fixed
 *  neutral monospace token — one look, no tone/tint/size/shape axis
 *  (`BadgeCodeProps`). `inherit`: text-current on a currentColor wash (a 1px
 *  currentColor border under high contrast) — no tone/tint, but size and
 *  shape still work (`BadgeInheritProps`). */
export type BadgeVariant = "soft" | "solid" | "outline" | "code" | "inherit";

/** `rounded` (default) 8px corner. `pill` is fully round, width following the
 *  content. `circle` is fully round on a fixed square box, sized by `size`
 *  (see `CIRCLE_BOX`) — for 1-2 characters; 3+ widen past the min-width
 *  instead of clipping. Read from `lib/shape.ts` (C7). */
export type BadgeShape = Extract<Shape, "rounded" | "pill" | "circle">;

/** `md` (default) reproduces today's look. `xs`/`sm` are denser; `lg` adds a
 *  larger gap. */
export type BadgeSize = "xs" | "sm" | "md" | "lg";

/** Circle box side, by size (C7: 14/20/24/28px). Each box also narrows its
 *  size's padding-x (cn() keeps this later px), so two tabular digits plus
 *  the 1px border fit the side and the box stays square. At xs even px-0
 *  leaves 12px, and two digits with dense-3's 0.02em tracking are 12.03px,
 *  so xs also drops the tracking. 3+ characters still widen. */
const CIRCLE_BOX: Record<BadgeSize, string> = {
  xs: "h-3.5 min-w-3.5 px-0 tracking-normal",
  sm: "h-5 min-w-5 px-0.5",
  md: "h-6 min-w-6 px-0.5",
  lg: "h-7 min-w-7 px-1",
};

/* A count past two characters widens beyond the disc, so it takes its size's
   text padding back (cn() keeps this later px) and its digits never touch the
   rim. Only a string or number child is measured. */
const CIRCLE_WIDE_PAD: Record<BadgeSize, string> = {
  xs: "px-1",
  sm: "px-1.5",
  md: "px-2",
  lg: "px-3",
};
const widensCircle = (children: React.ReactNode) =>
  (typeof children === "string" || typeof children === "number") &&
  String(children).length > 2;

/* variant="code": one fixed neutral token style — no tone, tint, size or
   shape axis (TypeScript errors on all four, BadgeCodeProps), so it never
   goes through badgeVariants()'s shape/size defaults. Keeps Badge's
   rounded-md corner (design-owner decision 10); the hand-built <code> chip
   it replaces was 4px. */
const CODE_CLASSES =
  "inline-flex items-center gap-1 rounded-md bg-pb-card-elevated text-pb-text font-mono font-normal text-dense-1 px-1.5 whitespace-nowrap";

export const badgeVariants = cva("inline-flex items-center gap-1 font-medium", {
  variants: {
    variant: {
      soft: "border",
      solid: "border",
      outline: "border bg-transparent",
      // No border by default — the wash carries the look. Under high
      // contrast the wash is replaced by a 1px currentColor border.
      inherit:
        "text-current bg-current/[calc(var(--pbds-opacity-soft)*100%)] hc:bg-transparent hc:border hc:border-current",
    },
    shape: {
      rounded: shapeClass("rounded"),
      pill: shapeClass("pill"),
      // Fully round, centred content, digits at a fixed width (no wobble
      // between e.g. "1" and "4" as a count changes).
      circle: `${shapeClass("circle")} justify-center tabular-nums`,
    },
    // `leading-none` sits at the END of every size string, after its
    // text-size class: cn() (tailwind-merge) treats a `text-*` size and
    // `leading-*` as the same conflict group, last-in-the-string wins, so
    // putting `leading-none` in the base (ahead of the size class in the
    // cva output order) silently dropped it (badge-shape-size finding).
    size: {
      xs: "text-dense-3 px-1 leading-none",
      sm: "text-dense-2 px-1.5 leading-none",
      md: "text-xs px-2 py-0.5 leading-none",
      lg: "text-body-small px-3 py-1.5 gap-2 leading-none",
    },
  },
  compoundVariants: [
    { shape: "circle", size: "xs", class: CIRCLE_BOX.xs },
    { shape: "circle", size: "sm", class: CIRCLE_BOX.sm },
    { shape: "circle", size: "md", class: CIRCLE_BOX.md },
    { shape: "circle", size: "lg", class: CIRCLE_BOX.lg },
  ],
  defaultVariants: { variant: "soft", shape: "rounded", size: "md" },
});

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Omitted: the neutral look; an untoned outline inherits its colours. */
  tone?: BadgeTone | undefined;
  /** `code` and `inherit` carry their own restricted prop sets
   *  (`BadgeCodeProps`, `BadgeInheritProps`) — see `BadgeComponent`'s
   *  overloads. */
  variant?: Exclude<BadgeVariant, "code" | "inherit"> | undefined;
  shape?: BadgeShape | undefined;
  size?: BadgeSize | undefined;
  /** A tint is decorative and never joins a tone: see `BadgeTintProps`. */
  tint?: undefined;
  /** Badge's one named capitals option (decision 14): adds `uppercase
   *  tracking-wide` and nothing else. Default `false`. Meant for
   *  `variant="inherit" size="xs"`; a TypeScript error with `variant="code"`
   *  (`BadgeCodeProps`) — code is one fixed token style. */
  uppercase?: boolean | undefined;
}

/** A categorically tinted Badge: decorative identity, not a state. Soft or
 *  outline only (a categorical solid fill fails white text), and never with a
 *  tone. Module tints are chrome, so a Badge refuses them (ADR-0092). */
export interface BadgeTintProps extends Omit<
  BadgeProps,
  "tone" | "variant" | "tint"
> {
  tint: CategoricalTint;
  tone?: undefined;
  variant?: "soft" | "outline" | undefined;
}

/** `variant="code"`: a fixed neutral monospace token (an inline `<code>`-like
 *  chip — SCIM secret fingerprints, hashes). `tone`, `tint`, `size` and
 *  `shape` are all TypeScript errors: code is one look, everywhere. */
export interface BadgeCodeProps extends Omit<
  BadgeProps,
  "tone" | "variant" | "tint" | "shape" | "size" | "uppercase"
> {
  variant: "code";
  tone?: undefined;
  tint?: undefined;
  shape?: undefined;
  size?: undefined;
  uppercase?: undefined;
}

/** `variant="inherit"`: text-current on a currentColor wash, so it takes the
 *  colour of whatever text surrounds it (a 1px currentColor border under
 *  high contrast). `tone` and `tint` are TypeScript errors — inherit IS the
 *  colour, there is no `tone="inherit"`. `size` and `shape` still work (the
 *  xs pill chip: the Draft sticky-note chip's replacement). */
export interface BadgeInheritProps extends Omit<
  BadgeProps,
  "tone" | "variant" | "tint"
> {
  variant: "inherit";
  tone?: undefined;
  tint?: undefined;
}

/* A tint paints its fill (soft only) and border at the tint alphas; the text
   stays pb-text, so contrast never rests on a decorative hue. */
function tintedClasses(variant: BadgeVariant, tint: CategoricalTint) {
  const recipe = CATEGORICAL_TINTS[tint];
  return [variant === "soft" && recipe.fill, recipe.line, "text-pb-text"];
}

/* Colour classes from the one tone map (lib/tones.ts). */
function toneClasses(variant: BadgeVariant, tone: BadgeTone | undefined) {
  // An untoned outline inherits the caller's colours: today's look.
  if (variant === "outline")
    return tone && [TONES[tone].border, TONES[tone].text];
  const recipe = TONES[tone ?? "neutral"];
  // Neutral never saturates, so an untoned or neutral solid is the soft
  // recipe. A saturated solid keeps its 1px border, filled, so the box does
  // not change size between variants.
  if (variant === "solid" && tone && tone !== "neutral")
    return ["border-transparent", recipe.solid];
  return recipe.soft;
}

const BadgeRoot = forwardRef<
  HTMLSpanElement,
  BadgeProps | BadgeTintProps | BadgeCodeProps | BadgeInheritProps
>(function Badge(
  {
    variant = "soft",
    tone,
    tint,
    shape,
    size,
    uppercase = false,
    className,
    children,
    ...rest
  },
  ref,
) {
  if (variant === "code") {
    return (
      <span
        ref={ref}
        className={cn(CODE_CLASSES, className)}
        {...rest}
        translate="no"
      >
        {children}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={cn(
        badgeVariants({ variant, shape, size }),
        shape === "circle" &&
          widensCircle(children) &&
          CIRCLE_WIDE_PAD[size ?? "md"],
        variant !== "inherit" &&
          (tint === undefined
            ? toneClasses(variant, tone)
            : tintedClasses(variant, tint)),
        uppercase && "uppercase tracking-wide",
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
});

type WithRef<P> = P & React.RefAttributes<HTMLSpanElement>;

/* Overloads carry the toned, tinted, code and inherit forms; the last one is
   the whole untinted API, so every other call resolves to it unchanged. */
export interface BadgeComponent {
  (props: WithRef<BadgeProps & { tone: BadgeTone }>): React.ReactNode;
  (props: WithRef<BadgeTintProps>): React.ReactNode;
  (props: WithRef<BadgeCodeProps>): React.ReactNode;
  (props: WithRef<BadgeInheritProps>): React.ReactNode;
  (props: WithRef<BadgeProps>): React.ReactNode;
  readonly $$typeof: symbol;
  displayName?: string | undefined;
}

export const Badge: BadgeComponent = BadgeRoot;
